import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getCoachProfile, updateCoachProfile } from '../../../features/coach/coachProfileSlice';
import { SubscriptionSettings } from './SubscriptionSettings';
import { FormInput } from '../../common/FormInput';
import { FormSelect } from '../../common/FormSelect';
import { PasswordResetModal } from './PasswordResetModal';
import { PasswordSetModal } from './PasswordSetModal';

export const CoachProfile = ({ onBack }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile, updateLoading, updateError, loading } = useSelector((state) => state.coachProfile);

  const [activeTab, setActiveTab] = useState('personal');
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [isPasswordSetModalOpen, setIsPasswordSetModalOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handlePasswordResetComplete = (token) => {
    setResetToken(token);
    setIsPasswordResetModalOpen(false);
    setIsPasswordSetModalOpen(true);
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    schoolType: '',
    division: '',
    conference: '',
    state: '',
    school: '',
    organization: '',
    jobTitle: ''
  });

  // Fetch coach profile on component mount
  useEffect(() => {
    dispatch(getCoachProfile());
  }, [dispatch]);

  // Load profile data when it's available
  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || '',
        schoolType: profile.schoolType || '',
        division: profile.division || '',
        conference: profile.conference || '',
        state: profile.state || '',
        school: profile.school || '',
        organization: profile.organization || '',
        jobTitle: profile.jobTitle || ''
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await dispatch(updateCoachProfile(formData)).unwrap();
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  // Options for dropdowns
  const schoolTypeOptions = ['University', 'College', 'Junior College', 'Community College', 'High School', 'Other'];
  const divisionOptions = ['Division I', 'Division II', 'Division III', 'NAIA', 'NJCAA Division I', 'NJCAA Division II', 'NJCAA Division III', 'CCCAA', 'Other'];
  const conferenceOptions = ['SEC', 'ACC', 'Big Ten', 'Big 12', 'Pac-12', 'Big East', 'American', 'Conference USA', 'MAC', 'Mountain West', 'Sun Belt', 'Other'];
  const stateOptions = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
  const jobTitleOptions = ['Head Coach', 'Assistant Coach', 'Pitching Coach', 'Hitting Coach', 'Recruiting Coordinator', 'Associate Head Coach', 'Other'];

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Content Area */}
      <div className="w-full border border-jp-line rounded-2xl shadow-xl p-4 md:p-8">
      {/* Back Button */}
      <button
        onClick={() => onBack ? onBack() : navigate(-1)}
        className="flex items-center gap-2 text-jp-muted hover:text-jp-ink mb-4 transition-colors"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
        </svg>
        <span className="font-medium">Back</span>
      </button>

      {/* Tabs */}
      <div className="flex w-full  overflow-x-auto">
        <button
          onClick={() => setActiveTab('personal')}
          className={`p-3 px-6 font-medium text-sm md:text-base rounded-2xl focus:outline-none transition-colors ${
            activeTab === 'personal'
              ? 'bg-cyan-500 text-white'
              : 'text-jp-ink hover:bg-jp-line'
          }`}
        >
          PERSONAL SETTINGS
        </button>
        <button
          onClick={() => setActiveTab('subscription')}
          className={`p-3 px-6 font-medium text-sm md:text-base rounded-2xl focus:outline-none transition-colors ${
            activeTab === 'subscription'
              ? 'bg-cyan-500 text-white'
              : 'text-jp-ink hover:bg-jp-line'
          }`}
        >
          SUBSCRIPTION SETTINGS
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'personal' ? (
        <form onSubmit={handleSubmit} className="p-4 md:p-8 flex flex-col gap-8 bg-[#121826] rounded-2xl shadow-xl mt-4">
          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Success Message */}
              {showSuccess && (
                <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg flex items-center gap-3">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-green-600 dark:text-green-400 font-medium">Profile updated successfully!</span>
                </div>
              )}

              {/* Error Message */}
              {updateError && (
                <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-3">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-600 dark:text-red-400 font-medium">{updateError}</span>
                </div>
              )}

              {/* Section 1: Personal Information */}
              <div>
            <h2 className="text-jp-ink text-base md:text-lg font-bold mb-5 text-left">
              UPDATE YOUR PERSONAL INFORMATION
            </h2>
            <div className="flex flex-col gap-4">
              <FormInput
                label="First name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
              />
              <FormInput
                label="Last name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
              />
              <FormInput
                label="E-mail"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="E-mail"
                disabled
              />
              <FormInput
                label="Phone Number"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="+1234567890"
              />
              <div>
                <div className="text-left mb-2">
                  <span className="text-jp-muted text-xs mr-1">You want to change your password?</span>
                  <button
                    type="button"
                    onClick={() => setIsPasswordResetModalOpen(true)}
                    className="text-jp-accent-ink text-xs hover:underline cursor-pointer"
                  >
                    Password reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Professional Info */}
          <div>
            <h2 className="text-jp-ink font-bold text-base md:text-lg mb-4 text-left">PROFESSIONAL INFORMATION</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <FormSelect
                label="Job Title"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                options={jobTitleOptions}
                placeholder
              />
              <FormInput
                label="School"
                name="school"
                value={formData.school}
                onChange={handleChange}
                placeholder="Enter school name"
              />
              <FormInput
                label="Organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                placeholder="Enter organization"
              />
              <FormSelect
                label="School Type"
                name="schoolType"
                value={formData.schoolType}
                onChange={handleChange}
                options={schoolTypeOptions}
                placeholder
              />
              <FormSelect
                label="Division"
                name="division"
                value={formData.division}
                onChange={handleChange}
                options={divisionOptions}
                placeholder
              />
              <FormSelect
                label="Conference"
                name="conference"
                value={formData.conference}
                onChange={handleChange}
                options={conferenceOptions}
                placeholder
              />
              <FormSelect
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
                options={stateOptions}
                placeholder
              />
            </div>
          </div>

              {/* Action Buttons */}
              <div className="flex flex-col-reverse md:flex-row md:justify-end gap-4 pt-2">
                <button
                  type="button"
                  onClick={() => navigate('/coach/dashboard')}
                  className="w-full md:w-auto px-7 py-2 rounded-lg border border-jp-line text-jp-ink text-base font-semibold bg-transparent hover:bg-[#1c273a] transition"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  disabled={updateLoading}
                  className="w-full md:w-auto px-7 py-2 rounded-lg bg-cyan-500 text-white text-base font-semibold hover:bg-cyan-600 shadow transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {updateLoading && (
                    <div className="w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  SAVE CHANGES
                </button>
              </div>
            </>
          )}
        </form>
      ) : (
        <SubscriptionSettings />
      )}

      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={isPasswordResetModalOpen}
        onClose={() => setIsPasswordResetModalOpen(false)}
        onComplete={handlePasswordResetComplete}
      />

      {/* Password Set Modal */}
      <PasswordSetModal
        isOpen={isPasswordSetModalOpen}
        onClose={() => setIsPasswordSetModalOpen(false)}
        resetToken={resetToken}
      />
      </div>
    </div>
  );
};
